import { useCallback, useState } from "react";

/**
 * Browser geolocation for web check-in (FEAT-2026-013).
 *
 * Wraps the callback-style Geolocation API in a promise, and — more usefully —
 * turns its four failure modes into messages someone can act on. The default
 * `PositionError.message` is often empty or browser-specific, so an employee
 * who cannot clock in would otherwise be told nothing.
 *
 * Deliberate non-features:
 *  - **No fallback position, ever.** Not (0,0), not the site centre, not a
 *    cached fix from last week. A punch records where somebody was; inventing
 *    that is worse than failing.
 *  - **No silent retry.** A denied permission does not fix itself by asking
 *    again in a loop; the user has to change a browser setting.
 */

export type GeoErrorKind =
  | "unsupported"
  | "insecure-context"
  | "permission-denied"
  | "unavailable"
  | "timeout";

export interface GeoPosition {
  latitude: number;
  longitude: number;
  /** Radius in metres the browser believes this fix is good to. */
  accuracy: number;
}

export interface GeoError {
  kind: GeoErrorKind;
  message: string;
}

const MESSAGES: Record<GeoErrorKind, string> = {
  unsupported: "This browser cannot provide your location. Use the mobile app to check in.",
  "insecure-context":
    "Location is only available over a secure (https) connection. Open the site with https and try again.",
  "permission-denied":
    "Location permission is blocked. Allow location for this site in your browser settings, then try again.",
  unavailable:
    "Your device could not determine a location. Check that location services are switched on, then try again.",
  timeout: "Getting your location took too long. Move somewhere with a clearer signal and try again.",
};

export function geoErrorFor(kind: GeoErrorKind): GeoError {
  return { kind, message: MESSAGES[kind] };
}

/**
 * `getCurrentPosition` as a promise. Rejects with a {@link GeoError}.
 *
 * `enableHighAccuracy` asks for GPS rather than the cheap network estimate,
 * and `maximumAge: 0` refuses a cached fix — a punch should reflect where the
 * employee is now, not where the browser last saw them.
 */
export function getCurrentPosition(timeoutMs = 15_000): Promise<GeoPosition> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(geoErrorFor("unsupported"));
      return;
    }
    // Chrome and Safari expose `navigator.geolocation` on http:// but always
    // fail the call. Detecting it up front gives a useful message instead of
    // a generic permission error.
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      reject(geoErrorFor("insecure-context"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          // Spec says accuracy is always a number, but a 0 or NaN from an odd
          // implementation would read as "perfectly accurate" — the one
          // direction we must not guess in. Treat it as unknown-but-coarse.
          accuracy:
            Number.isFinite(pos.coords.accuracy) && pos.coords.accuracy > 0
              ? pos.coords.accuracy
              : Number.POSITIVE_INFINITY,
        }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) reject(geoErrorFor("permission-denied"));
        else if (err.code === err.TIMEOUT) reject(geoErrorFor("timeout"));
        else reject(geoErrorFor("unavailable"));
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

/** What the browser says about location access for this site. */
export type GeoPermission = "granted" | "prompt" | "denied" | "unknown";

/**
 * May this page read location without asking? (BR-158)
 *
 * `navigator.permissions.query` only reports — it never prompts — so the Today
 * page can check location on load when the employee has already allowed it,
 * and otherwise wait to be asked. "unknown" covers browsers without the
 * Permissions API (older Safari) and any that throw for the name; the caller
 * then waits for the employee, which is the safe direction.
 */
export async function geolocationPermission(): Promise<GeoPermission> {
  try {
    const permissions =
      typeof navigator === "undefined" ? undefined : navigator.permissions;
    if (!permissions?.query) return "unknown";
    const { state } = await permissions.query({ name: "geolocation" as PermissionName });
    return state === "granted" || state === "prompt" || state === "denied" ? state : "unknown";
  } catch {
    return "unknown";
  }
}

export function isGeoError(e: unknown): e is GeoError {
  return (
    typeof e === "object" &&
    e !== null &&
    "kind" in e &&
    "message" in e &&
    typeof (e as GeoError).message === "string"
  );
}

export interface UseGeolocation {
  position: GeoPosition | null;
  error: GeoError | null;
  locating: boolean;
  /** Resolves to the fix, or null if it failed (the error lands in `error`). */
  locate: () => Promise<GeoPosition | null>;
  reset: () => void;
}

export function useGeolocation(timeoutMs = 15_000): UseGeolocation {
  const [position, setPosition] = useState<GeoPosition | null>(null);
  const [error, setError] = useState<GeoError | null>(null);
  const [locating, setLocating] = useState(false);

  const locate = useCallback(async () => {
    setLocating(true);
    setError(null);
    try {
      const pos = await getCurrentPosition(timeoutMs);
      setPosition(pos);
      return pos;
    } catch (e) {
      setPosition(null);
      setError(isGeoError(e) ? e : geoErrorFor("unavailable"));
      return null;
    } finally {
      setLocating(false);
    }
  }, [timeoutMs]);

  const reset = useCallback(() => {
    setPosition(null);
    setError(null);
  }, []);

  return { position, error, locating, locate, reset };
}
